import {Graph as CosmosGraph, GraphConfig, PointShape, getRgbaColor} from "@cosmos.gl/graph";
import {LabelOptions, LabelRenderer} from "@interacta/css-labels";

import {NetscopeCommonFlowviewComponent} from "@app/netscope/netscope-common-flowview/netscope-common-flowview.component";
import defaultColors, {
  ClustersViewerRenderer,
  VisualisationDataForRenderers
} from "@app/netscope/netscope-common-flowview/renderers/ClustersViewerRenderer";
import {NetscopeUtils} from "@app/netscope/netscope-utils";

/**
 * PROOF OF CONCEPT — cosmos.gl flavour of ClustersViewerRendererSigmaJS.
 *
 * Modelled on the cosmos.gl "Points / Point Labels" example
 * (https://cosmos.gl/?path=/story/examples-points--point-labels): flatten the graph into typed
 * arrays, hand them to cosmos.gl, and let the GPU do both the force layout and the drawing.
 * Labels are the one thing cosmos.gl does not draw, so — like the example — they are delegated to
 * @interacta/css-labels, which handles positioning and overlap culling on its own.
 *
 * The whole renderer is therefore: build arrays -> configure -> keep labels in sync.
 */

/**
 * cosmos.gl clamps every point to the `[0, spaceSize]` square on each tick, so a layout that
 * outgrows it does not keep spreading: its outer points pile up along the border. The space is
 * therefore sized per dataset (see `spaceSizeFor`), between these two bounds. The upper bound
 * follows the cosmos.gl warning that larger spaces crash some devices; it is lowered further on
 * GPUs whose textures cannot hold it.
 */
const MIN_SPACE_SIZE = 4096;
const MAX_SPACE_SIZE = 8192;

/**
 * Room kept between the predicted edge of the layout and the border of the space: the prediction
 * assumes an evenly-filled disc, and the cloud briefly overshoots while it expands.
 */
const SPACE_MARGIN = 1.3;

const REPULSION = 5.8;
const BASE_GRAVITY = 0.10;

/**
 * Nodes are drawn as white discs with a coloured ring, like the SigmaJS renderer's bordered nodes.
 * cosmos.gl has no per-point border — `outlinedPointRingColor` is one global colour — so each
 * (category, size) pair gets a small generated texture instead, applied through `setImageData`.
 */
const NODE_FILL_COLOR = "#FFFFFF";

/**
 * Wanted ring thickness in screen pixels at zoom 1. The texture is stretched over the node, so this
 * is only honoured at that zoom: zoom in and the ring thickens with everything else.
 */
const NODE_BORDER_PX = 2;

/** Texture resolution of a node. Large enough to stay crisp when zoomed well past 1. */
const NODE_IMAGE_RESOLUTION = 128;

/**
 * Node diameter in space units per unit of `log10(degree)`; a node renders `2 * size` pixels wide
 * at zoom 1. Raised from the value used while nodes were flat discs: a 4.5px ring simply does not
 * fit inside a 16px node. Collision spacing, link distance and the label size thresholds are all
 * derived from node sizes, so they follow this automatically.
 */
const NODE_SIZE_SCALE = 8;

/**
 * Node sizes are snapped to this step so that a handful of textures covers the whole graph — one
 * per (category, size) pair actually in use, rather than one per node.
 */
const NODE_SIZE_STEP = 4;

/**
 * Labels are DOM elements repositioned on every tick, so the number of candidates has to be capped.
 * The candidates are re-picked from whatever is currently *in view* (see `refreshTrackedLabels`),
 * so this bounds the per-tick work without ever making a node permanently unlabellable.
 */
const MAX_TRACKED_LABELS = 1500;

/** Minimum delay between two viewport queries, which each cost a GPU readback. */
const TRACKED_LABELS_REFRESH_MS = 200;

/**
 * Gap kept between the visual edges of two settled points, in space units (see the cosmos.gl
 * "Forces / Collision" example). Points end up `2 * COLLISION_PADDING` apart.
 */
const COLLISION_PADDING = 4;

/**
 * Where a node's name is drawn: centred "inside" the node, or "side" — just past its right edge.
 * Inside keeps the graph tidy but caps how much text a node can carry; beside is readable whatever
 * the name length, at the cost of ink spilling between the nodes.
 */
const LABEL_PLACEMENT: "inside" | "side" = "side";

/** @interacta/css-labels estimates text width as `fontSize * this ratio * text.length`. */
const FONT_WIDTH_RATIO = 0.6;

/**
 * "inside" only. Fraction of a node's on-screen diameter the text may occupy. Below 1 because the
 * widest chord of a circle is its diameter, but the text also has height: 0.8 keeps it clear of
 * the curved edges.
 */
const LABEL_FIT_RATIO = 0.8;

/**
 * "side" only. Font size as a fraction of the node's on-screen diameter. Nothing constrains the
 * width out there, so this is purely how loudly a node gets to announce itself.
 */
const SIDE_LABEL_SIZE_RATIO = 0.5;

/** "side" only. Pixels between the node's edge and the start of the text. */
const SIDE_LABEL_GAP = 4;

/**
 * Font size band, in pixels. The node's size decides where in this band its label lands, and a node
 * too small to reach the bottom of it gets no label at all.
 *
 * The band is deliberately narrow. Overlapping labels are resolved by hiding the lighter one, so a
 * big label does not just take room — it suppresses every label it covers. Letting hub labels grow
 * to twice the size of the rest was costing far more labels than it was worth.
 */
const MIN_LABEL_FONT_SIZE = 6;
const MAX_LABEL_FONT_SIZE = 13;

/**
 * Names longer than this are elided. A long name makes a wide label, and a wide label blankets the
 * labels of everything beside it — one 60-character name can clear a whole neighbourhood.
 */
const MAX_LABEL_CHARS = 24;

/**
 * Hovering a node dims everything outside its neighbourhood, as the SigmaJS renderer does. That
 * renderer hides the rest outright; cosmos.gl greys it out instead, and these are the opacities it
 * falls back to — low enough to push the remainder into the background, but not zero, so the shape
 * of the graph stays as context around the neighbourhood. Set them to 0 to hide it as SigmaJS does.
 */
const GREYOUT_POINT_OPACITY = 0.15;
const GREYOUT_LINK_OPACITY = 0.05;

type CosmosGraphData = {
  pointPositions: Float32Array;
  pointColors: Float32Array;
  pointSizes: Float32Array;
  links: Float32Array;
  linkColors: Float32Array;
  /** Every point is shape-less: what you see is its image (a white disc with a coloured ring). */
  pointShapes: Float32Array;
  pointImageIndices: Float32Array;
  nodeImages: ImageData[];
  /** point index -> node uuid / name / degree. cosmos.gl only ever speaks indices. */
  pointIds: string[];
  pointNames: string[];
  pointDegrees: number[];
  /** Label colour per point, used by "inside" placement: the category colour, on a white node. */
  pointLabelColors: string[];
  /** link index -> its two point indices, to build the side-panel query on click. */
  linkEndpoints: [number, number][];
  /** Side of the simulation square, and the gravity needed to keep the layout inside it. */
  spaceSize: number;
  gravity: number;
};

export class ClustersViewerRendererCosmosGL extends ClustersViewerRenderer {

  cosmosGraph: CosmosGraph;
  labelRenderer: LabelRenderer;
  labelsDiv: HTMLDivElement;
  data: CosmosGraphData;
  interval;

  /** The div holding the canvas cosmos.gl draws on and the labels stacked above it. */
  private container: HTMLDivElement;

  /** Theme-derived label styling, resolved once per render. Only "side" placement uses these. */
  private labelTextColor: string;
  private labelStyle: string;
  private labelHaloColor: string;

  private lastTrackedLabelsRefresh = 0;
  private trackedLabelsRefreshTimeout;

  /**
   * Point indices currently highlighted by hover, or `undefined` when nothing is hovered. cosmos.gl
   * owns the greying of the canvas; this is the same set on our side, because the labels are DOM
   * elements it knows nothing about and they have to be dimmed with the nodes they belong to.
   */
  private hoveredNeighbourhood: Set<number> | undefined;

  /** Same buckets as the SigmaJS renderer, reusing the shared `colors` palette. */
  private categoryOf(type: string, uuid: string, focusedUuids: Set<string>): string {
    if (focusedUuids.has(uuid)) return "focused";
    if (type === "vm" || type === "host") return "infrastructure";
    if (type === "external_ip") return "external_ip";
    if (type === "asn" || type === "internet_ip") return "internet_ip";
    return "unknown_ip";
  }

  /**
   * Smallest link distance that still clears the collision radii of the two biggest points.
   * A point's collision radius is half its size, plus the padding, so two touching points need
   * `maxSize + 2 * padding` between their centres; the story leaves ~50% of headroom on top.
   */
  private minimumLinkDistance(pointSizes: Float32Array): number {
    let maxPointSize = 0;
    for (const size of pointSizes) {
      maxPointSize = Math.max(maxPointSize, size);
    }
    return Math.max(20, (maxPointSize + 2 * COLLISION_PADDING) * 1.5);
  }

  /**
   * Radius of the disc the points are seeded in: big enough that the sum of their collision discs
   * covers only a fraction of it, so they start out mostly disjoint whatever the dataset size.
   */
  private seedRadius(pointSizes: Float32Array): number {
    let collisionArea = 0;
    for (const size of pointSizes) {
      const radius = size / 2 + COLLISION_PADDING;
      collisionArea += Math.PI * radius * radius;
    }
    // x4 the strictly-needed area leaves room for the layout to breathe.
    return Math.max(50, Math.sqrt((4 * collisionArea) / Math.PI));
  }

  /**
   * Radius the layout settles at, where repulsion and gravity balance at its edge. cosmos.gl's
   * repulsion falls off as `repulsion / d`, so — as with 2D electrostatics — `n` points spread over
   * a disc of radius `r` push its edge outwards with `repulsion * n / r`, while gravity pulls it back
   * with `0.1 * gravity * r` (see the cosmos.gl force shaders). They balance at the radius below:
   * it grows with the *number* of points, whatever their size, and shrinks with `sqrt(gravity)`.
   * The collision force puts a floor under it: the points' own discs must fit in the layout.
   */
  private settledRadius(pointCount: number, gravity: number, seedRadius: number): number {
    // `seedRadius` holds x4 the collision area, so half of it is the tightest packing.
    return Math.max(Math.sqrt((10 * REPULSION * pointCount) / gravity), seedRadius / 2);
  }

  /**
   * Picks the space and gravity for a dataset: the smallest power of two (cosmos.gl builds its
   * repulsion quadtree on `log2(spaceSize)` levels) that holds the settled layout, within the bounds
   * the device can afford. When even the largest space is too small, gravity is raised until the
   * layout fits in it — by the *square* of the missing room, since the radius only shrinks with
   * `sqrt(gravity)`.
   */
  private spaceFor(pointCount: number, seedRadius: number): {spaceSize: number, gravity: number} {
    const requiredSpaceSize = 2 * SPACE_MARGIN * this.settledRadius(pointCount, BASE_GRAVITY, seedRadius);
    const maxSpaceSize = Math.min(MAX_SPACE_SIZE, ClustersViewerRendererCosmosGL.maxTextureSize() / 2);
    let spaceSize = MIN_SPACE_SIZE;
    while (spaceSize < requiredSpaceSize && spaceSize * 2 <= maxSpaceSize) {
      spaceSize *= 2;
    }
    if (requiredSpaceSize <= spaceSize) {
      return {spaceSize, gravity: BASE_GRAVITY};
    }
    const targetRadius = spaceSize / 2 / SPACE_MARGIN;
    if (seedRadius / 2 > targetRadius) {
      // No gravity can help here: the nodes themselves cover more than the space.
      console.warn(`ClustersViewerRendererCosmosGL: ${pointCount} nodes do not fit in a ${spaceSize} space`);
    }
    const gravity = (10 * REPULSION * pointCount) / (targetRadius * targetRadius);
    return {spaceSize, gravity: Math.max(BASE_GRAVITY, gravity)};
  }

  private static cachedMaxTextureSize: number | undefined;

  /**
   * cosmos.gl silently halves a `spaceSize` that reaches the GPU's texture limit, which would leave
   * our seeds (placed around the centre of the space we asked for) in a corner of the real one.
   * The limit is read up front instead, so the space we pick is the space we get.
   */
  private static maxTextureSize(): number {
    if (this.cachedMaxTextureSize === undefined) {
      let size = 2 * MIN_SPACE_SIZE;
      try {
        const gl = document.createElement("canvas").getContext("webgl2");
        if (gl) {
          size = gl.getParameter(gl.MAX_TEXTURE_SIZE);
          gl.getExtension("WEBGL_lose_context")?.loseContext();
        }
      } catch {
        // Keep the conservative default.
      }
      this.cachedMaxTextureSize = size;
    }
    return this.cachedMaxTextureSize;
  }

  /**
   * Draws one node: a white disc with a coloured ring, as an `ImageData` cosmos.gl can upload.
   *
   * The texture is stretched across the node's whole footprint, so the ring thickness is a
   * *fraction* of the node rather than a pixel count. A node of `size` renders `2 * size` pixels
   * wide at zoom 1, which is what turns the wanted pixel thickness into a fraction here — and why
   * each node size needs its own texture to keep that thickness constant across sizes.
   */
  private drawNodeImage(borderColor: string, size: number): ImageData {
    const resolution = NODE_IMAGE_RESOLUTION;
    const canvas = document.createElement("canvas");
    canvas.width = resolution;
    canvas.height = resolution;
    const context = canvas.getContext("2d");

    // Leave a pixel of margin so the outer edge of the stroke is not clipped by the texture.
    const outerRadius = resolution / 2 - 1;
    // Capped so that a small node degrades to a solid disc instead of a ring wider than its radius.
    const borderWidth = Math.min(outerRadius, resolution * (NODE_BORDER_PX / (2 * size)));

    // Stroke is centred on the path, so a path at `outerRadius - borderWidth / 2` puts its outer
    // edge exactly on `outerRadius`. Filling the same path colours everything inside the ring.
    context.beginPath();
    context.arc(resolution / 2, resolution / 2, outerRadius - borderWidth / 2, 0, 2 * Math.PI);
    context.fillStyle = NODE_FILL_COLOR;
    context.fill();
    context.lineWidth = borderWidth;
    context.strokeStyle = borderColor;
    context.stroke();

    return context.getImageData(0, 0, resolution, resolution);
  }

  /** Flattens the visualisation data straight into the typed arrays cosmos.gl consumes. */
  private buildGraphData(visualisationData: VisualisationDataForRenderers): CosmosGraphData {
    const focusedUuids = new Set(visualisationData.focusedVmsUuids);

    // At most one link per pair of resources, carrying the summed exchanged bytes. The weighted
    // degree collected here drives both the point size and which labels are worth tracking.
    const mergedLinks = new Map<string, { source: string, target: string, exchangedBytes: number }>();
    const degrees = new Map<string, number>();

    for (const link of visualisationData.links) {
      const key = [link.source.uuid, link.target.uuid].sort().join("__");
      let merged = mergedLinks.get(key);
      if (merged === undefined) {
        merged = {source: link.source.uuid, target: link.target.uuid, exchangedBytes: 0};
        mergedLinks.set(key, merged);
      }
      merged.exchangedBytes += link.getExchangedBytes();

      const weight = link.subflows.length > 0 ? link.subflows.length : 1;
      for (const uuid of [link.source.uuid, link.target.uuid]) {
        degrees.set(uuid, (degrees.get(uuid) ?? 0) + weight);
      }
    }

    // ---- Points
    const pointIndexById = new Map<string, number>();
    const pointIds: string[] = [];
    const pointNames: string[] = [];
    const pointDegrees: number[] = [];
    const pointLabelColors: string[] = [];

    const pointPositions = new Float32Array(visualisationData.vms.length * 2);
    const pointColors = new Float32Array(visualisationData.vms.length * 4);
    const pointSizes = new Float32Array(visualisationData.vms.length);
    const pointShapes = new Float32Array(visualisationData.vms.length);
    const pointImageIndices = new Float32Array(visualisationData.vms.length);

    // One texture per (category, size) pair actually in use; `imageKeys` maps a pair to its index
    // in `nodeImages`, which is what `setPointImageIndices` addresses.
    const nodeImages: ImageData[] = [];
    const imageKeys = new Map<string, number>();

    visualisationData.vms.forEach((vm, index) => {
      pointIndexById.set(vm.uuid, index);
      pointIds.push(vm.uuid);
      pointNames.push(vm.name);
      pointDegrees.push(degrees.get(vm.uuid) ?? 0);

      const category = this.categoryOf(vm.type, vm.uuid, focusedUuids);
      const borderColor = defaultColors[category];
      // A white node carries no category colour of its own, so the ring is the whole encoding —
      // and an "inside" label picks the same colour to stay tied to it.
      pointColors.set(getRgbaColor(NODE_FILL_COLOR), index * 4);
      pointLabelColors.push(borderColor);

      // Snapped to NODE_SIZE_STEP so that a handful of textures covers every node.
      const rawSize = NODE_SIZE_SCALE * Math.max(1, NetscopeUtils.safeLog(degrees.get(vm.uuid)));
      const size = Math.max(NODE_SIZE_STEP, Math.round(rawSize / NODE_SIZE_STEP) * NODE_SIZE_STEP);
      pointSizes[index] = size;

      // The image *is* the node, so nothing is drawn underneath it.
      pointShapes[index] = PointShape.None;

      const imageKey = `${category}|${size}`;
      let imageIndex = imageKeys.get(imageKey);
      if (imageIndex === undefined) {
        imageIndex = nodeImages.length;
        nodeImages.push(this.drawNodeImage(borderColor, size));
        imageKeys.set(imageKey, imageIndex);
      }
      pointImageIndices[index] = imageIndex;
    });

    // Seed the points in a disc just big enough to hold them all without overlapping, rather than
    // over a fixed area: the collision force then only has to nudge them instead of untangling a
    // pile (or waiting for gravity to reel in a cloud that was scattered far too wide).
    const seedRadius = this.seedRadius(pointSizes);
    const {spaceSize, gravity} = this.spaceFor(visualisationData.vms.length, seedRadius);
    const seedDiscRadius = Math.min(seedRadius, spaceSize / 2 / SPACE_MARGIN);
    for (let index = 0; index < visualisationData.vms.length; index++) {
      const angle = Math.random() * Math.PI * 2;
      // sqrt() keeps the density uniform across the disc instead of clumping at the centre.
      const radius = Math.sqrt(Math.random()) * seedDiscRadius;
      pointPositions[index * 2] = spaceSize / 2 + Math.cos(angle) * radius;
      pointPositions[index * 2 + 1] = spaceSize / 2 + Math.sin(angle) * radius;
    }

    // ---- Links (endpoints we did not keep would shift every following index, so drop them)
    const keptLinks = [...mergedLinks.values()]
      .filter((l) => pointIndexById.has(l.source) && pointIndexById.has(l.target));

    const links = new Float32Array(keptLinks.length * 2);
    const linkColors = new Float32Array(keptLinks.length * 4);
    const linkEndpoints: [number, number][] = [];

    keptLinks.forEach((link, index) => {
      const source = pointIndexById.get(link.source);
      const target = pointIndexById.get(link.target);

      links[index * 2] = source;
      links[index * 2 + 1] = target;
      linkEndpoints.push([source, target]);

      linkColors.set(getRgbaColor(NetscopeUtils.exchangedBytesToColorHex(
        link.exchangedBytes, visualisationData.minBytes, visualisationData.maxBytes
      )), index * 4);
    });

    return {
      pointPositions, pointColors, pointSizes, links, linkColors,
      pointShapes, pointImageIndices, nodeImages,
      pointIds, pointNames, pointDegrees, pointLabelColors, linkEndpoints,
      spaceSize, gravity
    };
  }

  override async render(flowComponent: NetscopeCommonFlowviewComponent) {
    this.flowComponent = flowComponent;

    // ---- Data fetching: strictly the same as the SigmaJS renderer -------------------------------
    const whereClause = this.generateWhereClause(flowComponent);

    this.flowComponent.datatableTabFlowsRepository = this.flowComponent.flowsRepository.clone();

    if (flowComponent.ignoredPorts.length > 0 && !flowComponent.showIgnoredPorts) {
      const portClauses = flowComponent.ignoredPorts.map((p) => ` dst_port != ${p} `);
      const initWhereClause = `${this.flowComponent.datatableTabFlowsRepository.whereClause} and (${portClauses.join(" and ")}) and (${whereClause})`;
      await this.flowComponent.datatableTabFlowsRepository.init(initWhereClause);
    }

    this.flowComponent.datatableTabFlowsRepository.setWhereClause(whereClause);
    this.flowComponent.datatableTabFlowsRepository.showFirstPage();

    const graphData = await this.flowComponent.datatableTabFlowsRepository.generateGraphData();
    const visualisationData = this.computeVisualisationDataForRenderers(graphData, flowComponent);
    this.data = this.buildGraphData(visualisationData);

    // ---- Labels layer, stacked above the canvas ------------------------------------------------
    const container = document.getElementById("divFlowsSvg") as HTMLDivElement;
    this.container = container;
    const textColor = flowComponent.isDarkMode ? "#FFF" : "#000";

    this.labelsDiv = document.createElement("div");
    container.appendChild(this.labelsDiv);
    // Every label overrides its own colour and font size, so the renderer gets no defaults here.
    this.labelRenderer = new LabelRenderer(this.labelsDiv, {pointerEvents: "none"});

    // Drop the library's chip background and brightness filter — the node, or the canvas, is the
    // background. Beside a node the text crosses links, so it gets a halo in the canvas colour.
    this.labelTextColor = textColor;
    const haloColor = flowComponent.isDarkMode ? "#222" : "#FFF";
    this.labelHaloColor = LABEL_PLACEMENT === "side" ? haloColor : undefined;
    this.labelStyle = "background: transparent; line-height: 1; font-weight: 600; filter: none"
      + (LABEL_PLACEMENT === "side" ? `; text-shadow: 0 0 3px ${haloColor}, 0 0 3px ${haloColor}` : "");

    // ---- The cosmos.gl instance ----------------------------------------------------------------
    const config: GraphConfig = {
      spaceSize: this.data.spaceSize,
      backgroundColor: flowComponent.isDarkMode ? "#222222" : "#ffffff",

      scalePointsOnZoom: true,
      renderHoveredPointRing: true,
      hoveredPointRingColor: textColor,
      hoveredPointCursor: "pointer",
      hoveredLinkCursor: "pointer",

      // Hover highlight: see `highlightNeighbourhood`. Everything outside the hovered neighbourhood
      // is greyed out down to these opacities.
      pointGreyoutOpacity: GREYOUT_POINT_OPACITY,
      linkGreyoutOpacity: GREYOUT_LINK_OPACITY,
      onPointMouseOver: (index: number) => this.highlightNeighbourhood(index),
      onPointMouseOut: () => this.clearHighlight(),
      onLinkMouseOver: (linkIndex: number) => this.highlightLinkNeighbourhood(linkIndex),
      onLinkMouseOut: () => this.clearHighlight(),

      curvedLinks: false,
      linkOpacity: 0.6,
      linkVisibilityMinTransparency: 0.6,
      linkDefaultWidth: 1.3,

      // Force profile from the cosmos.gl "Forces / Collision" example: the collision force is what
      // actually keeps points from overlapping, so gravity and the link spring are kept weak enough
      // not to fight it, and the simulation is given a slow decay to let it resolve.
      simulationCollision: 0.95,
      simulationCollisionPadding: COLLISION_PADDING,
      // `undefined` => the collision radius of each point is half of its own size.
      simulationCollisionRadius: undefined,
      simulationRepulsion: REPULSION,
      simulationGravity: this.data.gravity,
      simulationCluster: 0.01,
      simulationLinkSpring: 0.5,
      // The spring must not pull two connected points closer than their collision radii allow,
      // otherwise they pile up in a knot the collision force can never undo.
      simulationLinkDistance: this.minimumLinkDistance(this.data.pointSizes),
      simulationFriction: 0.85,
      simulationDecay: 1000,

      enableDrag: flowComponent.draggableNodesEnabled,
      fitViewOnInit: true,
      fitViewDelay: 500,

      onSimulationTick: () => this.updateLabels(),
      // Panning brings into view nodes that were never label candidates, so the candidates are
      // re-picked *during* the gesture as well. `refreshTrackedLabels` throttles itself, so this
      // costs at most one viewport query per TRACKED_LABELS_REFRESH_MS however fast the pan is.
      onZoom: () => {
        this.updateLabels();
        this.refreshTrackedLabels();
      },
      // Zooming and panning both end here, and the simulation moves nodes across the viewport
      // while it runs: each is a moment where a different set of nodes became visible.
      onZoomEnd: () => this.refreshTrackedLabels(),
      onSimulationEnd: () => this.refreshTrackedLabels(),

      onPointClick: (index: number) => this.onPointClicked(index, flowComponent, graphData),
      onLinkClick: (linkIndex: number) => this.onLinkClicked(linkIndex, flowComponent)
    };

    this.cosmosGraph = new CosmosGraph(container, config);

    this.cosmosGraph.setPointPositions(this.data.pointPositions);
    this.cosmosGraph.setPointColors(this.data.pointColors);
    this.cosmosGraph.setPointSizes(this.data.pointSizes);
    // Shape `None` everywhere: the ring texture is the node. Image size defaults to the point size,
    // so the texture lands exactly on the node's footprint.
    this.cosmosGraph.setPointShapes(this.data.pointShapes);
    this.cosmosGraph.setImageData(this.data.nodeImages);
    this.cosmosGraph.setPointImageIndices(this.data.pointImageIndices);
    this.cosmosGraph.setLinks(this.data.links);
    this.cosmosGraph.setLinkColors(this.data.linkColors);
    this.cosmosGraph.render();

    // Pick the first batch of label candidates as soon as the device is up; `fitViewOnInit` will
    // fire `onZoomEnd` shortly after and re-pick them for the framed viewport.
    this.cosmosGraph.ready.then(() => this.refreshTrackedLabels());

    this.cosmosGraph.start(1);

    // ---- Play/pause button + renderer lifecycle, mirroring the SigmaJS renderer -----------------
    this.interval = setInterval(() => {
      if (this.cosmosGraph === undefined) return;

      if (flowComponent.renderer !== this) {
        this.destroy(flowComponent);
        return;
      }

      if (flowComponent.stopComputingPositionsRequested) {
        this.cosmosGraph.pause();
      } else if (flowComponent.reComputingPositionsRequested) {
        // start() also reheats an already-running simulation, which is what we want here.
        this.cosmosGraph.start(0.5);
      }
      flowComponent.stopComputingPositionsRequested = false;
      flowComponent.reComputingPositionsRequested = false;

      flowComponent.isComputingPositions = this.cosmosGraph.isSimulationRunning;

      // The drag toggle lives in the top menu and can flip at any time.
      if (this.cosmosGraph.config.enableDrag !== flowComponent.draggableNodesEnabled) {
        this.cosmosGraph.setConfigPartial({enableDrag: flowComponent.draggableNodesEnabled});
      }
    }, 100);

    flowComponent.updateLegend(visualisationData);
  }

  /**
   * Re-picks which nodes are label candidates, from whatever is currently in the viewport.
   *
   * Only tracked points have readable positions, so only they can be labelled. Picking them once,
   * as a global top-N by degree, meant the low-degree nodes around the edge of the infrastructure
   * were never candidates and so stayed unlabelled however far you zoomed in. Re-picking from the
   * viewport ties the cap to what is actually on screen instead: zoom into a quiet corner and the
   * nodes there become the candidates.
   */
  private refreshTrackedLabels() {
    // `findPointsInRect` is a GPU readback and must not run before the device is up.
    if (this.cosmosGraph === undefined || !this.cosmosGraph.isReady) return;

    const elapsed = Date.now() - this.lastTrackedLabelsRefresh;
    if (elapsed < TRACKED_LABELS_REFRESH_MS) {
      // Throttled, but the *last* call of a burst has to land: it is the one carrying the viewport
      // the user actually stopped on. Dropping it would leave the candidates from before the zoom.
      if (this.trackedLabelsRefreshTimeout === undefined) {
        this.trackedLabelsRefreshTimeout = setTimeout(() => {
          this.trackedLabelsRefreshTimeout = undefined;
          this.refreshTrackedLabels();
        }, TRACKED_LABELS_REFRESH_MS - elapsed);
      }
      return;
    }
    this.lastTrackedLabelsRefresh = Date.now();

    const width = this.labelsDiv.offsetWidth;
    const height = this.labelsDiv.offsetHeight;
    if (width === 0 || height === 0) return;

    // The rect is in canvas coordinates, so the whole viewport is simply its two corners.
    let visible = this.cosmosGraph.findPointsInRect([[0, 0], [width, height]]);
    // An empty result means the readback found nothing useful: keep the previous candidates rather
    // than blanking every label.
    if (visible.length === 0) return;

    if (visible.length > MAX_TRACKED_LABELS) {
      visible = visible
        .sort((a, b) => this.data.pointDegrees[b] - this.data.pointDegrees[a])
        .slice(0, MAX_TRACKED_LABELS);
    }

    this.cosmosGraph.trackPointPositionsByIndices(visible);
    // Only tracked points have readable positions, so a point that just became a candidate carries
    // no label until the labels are redrawn — and nothing else would do it. `updateLabels` runs on
    // a simulation tick or a zoom, and this method runs precisely when both have stopped: at the
    // end of a gesture, or when the simulation settles. Without this the new candidates stay blank
    // until some unrelated event happens to redraw them.
    this.updateLabels();
  }

  /**
   * Hover highlight, mirroring the SigmaJS renderer: the hovered node, its neighbours and the links
   * between them keep their appearance, everything else is greyed out.
   *
   * cosmos.gl does the work itself — `highlightedPointIndices` / `highlightedLinkIndices` grey out
   * everything *not* listed — so this only has to name the neighbourhood. `getNeighboringPointIndices`
   * and `getConnectedLinkIndices` read the adjacency cosmos.gl built from `setLinks`, which is the
   * merged one-link-per-pair graph, so a neighbour is a resource this one exchanged traffic with.
   *
   * Note that the greyout of a point rendered from an image (which is every point here — the ring
   * texture *is* the node) blends the texture toward the background and applies
   * `pointGreyoutOpacity`; `pointGreyoutColor` is ignored on that path, so it is not set.
   */
  private highlightNeighbourhood(pointIndex: number) {
    if (this.cosmosGraph === undefined) return;

    const points = [pointIndex, ...this.cosmosGraph.getNeighboringPointIndices(pointIndex)];

    // `getConnectedLinkIndices` returns the links of the *induced subgraph* of the set it is given
    // — a link is only returned if **both** its endpoints are in that set — so it has to be handed
    // the whole neighbourhood, not just the hovered point. Handing it the single point matches
    // nothing short of a self-loop, and an empty list does not mean "no highlighting": it means
    // "highlighting is on and nothing is highlighted", which greys every link out.
    const links = this.cosmosGraph.getConnectedLinkIndices(points)
      // That subgraph also holds the links *between* neighbours. As in the SigmaJS renderer, only
      // the ones touching the hovered node are kept: they are the flows a click would list.
      .filter((linkIndex) => {
        const endpoints = this.data.linkEndpoints[linkIndex];
        return endpoints !== undefined && (endpoints[0] === pointIndex || endpoints[1] === pointIndex);
      });

    this.applyHighlight(points, links);
  }

  /** Same for a hovered link: the link itself and the two resources it connects. */
  private highlightLinkNeighbourhood(linkIndex: number) {
    if (this.cosmosGraph === undefined) return;

    this.applyHighlight(this.cosmosGraph.getConnectedPointIndices(linkIndex), [linkIndex]);
  }

  private applyHighlight(pointIndices: number[], linkIndices: number[]) {
    this.hoveredNeighbourhood = new Set(pointIndices);
    this.cosmosGraph.setConfigPartial({
      highlightedPointIndices: pointIndices,
      highlightedLinkIndices: linkIndices
    });
    // The labels are not driven by the simulation, so they would otherwise only catch up on the
    // next tick — and not at all once the simulation has settled.
    this.updateLabels();
  }

  /**
   * Back to the whole graph. `undefined` is what clears the greyout: an empty array means
   * "highlighting is on and nothing is highlighted", which greys out everything.
   *
   * cosmos.gl fires the mouse-out callbacks before the mouse-over ones within a single hover pass,
   * so moving straight from one node to the next clears and re-applies in that order rather than
   * clearing the highlight that was just set.
   */
  private clearHighlight() {
    if (this.cosmosGraph === undefined) return;

    this.hoveredNeighbourhood = undefined;
    this.cosmosGraph.setConfigPartial({
      highlightedPointIndices: undefined,
      highlightedLinkIndices: undefined
    });
    this.updateLabels();
  }

  /**
   * Tracked positions -> screen -> labels, as in the cosmos.gl point-labels example, except the
   * label is tied to the size of its node rather than drawn at a fixed size above it.
   *
   * Node sizes keep encoding the degree, so the text adapts instead: it is scaled from the node's
   * on-screen diameter and dropped entirely once that would be too small to read. Because
   * `scalePointsOnZoom` is on, a node grows as you zoom in and its label grows with it — so the
   * graph reads as unlabelled from afar and labels itself as you move in.
   *
   * How *many* labels survive is mostly a question of how much room each one takes, since the
   * renderer resolves an overlap by hiding one of the two. Hence the narrow font band and the
   * elision of long names: both buy label count.
   *
   * `LABEL_PLACEMENT` picks where the text goes; see the two branches below.
   */
  private updateLabels() {
    if (this.cosmosGraph === undefined || !this.cosmosGraph.isReady) return;

    const viewportWidth = this.labelsDiv.offsetWidth;
    const labels: LabelOptions[] = [];
    this.cosmosGraph.getTrackedPointPositionsMap().forEach((position, pointIndex) => {
      // A greyed-out node with a full-strength label beside it reads as highlighted, which defeats
      // the point of the highlight — so outside the hovered neighbourhood the label goes too.
      if (this.hoveredNeighbourhood !== undefined && !this.hoveredNeighbourhood.has(pointIndex)) return;

      const name = this.data.pointNames[pointIndex] ?? "";
      if (name.length === 0) return;
      // Elided before the width is derived from it, so a long name costs no extra room.
      const text = name;

      // `spaceToScreenRadius` folds in the current zoom, so this is a live pixel measurement.
      const screenDiameter = 2 * this.cosmosGraph.spaceToScreenRadius(
        this.cosmosGraph.getPointRadiusByIndex(pointIndex) ?? 0
      );

      const fontSize = Math.min(MAX_LABEL_FONT_SIZE, LABEL_PLACEMENT === "inside"
        // Inside: the text has to fit across the node, so longer names get a smaller font.
        ? (screenDiameter * LABEL_FIT_RATIO) / (FONT_WIDTH_RATIO * text.length)
        // Beside: nothing constrains the width, so the font just tracks the node's size.
        : screenDiameter * SIDE_LABEL_SIZE_RATIO);
      if (fontSize < MIN_LABEL_FONT_SIZE) return;

      const screenPosition = this.cosmosGraph.spaceToScreenPosition([position?.[0] ?? 0, position?.[1] ?? 0]);

      // The library centres a label on `x` and anchors it by its *bottom* edge, so `y` always gets
      // half a line pushed down to centre it on the node. `line-height: 1` makes that height
      // exactly `fontSize`, and with no padding the width is the library's own estimate.
      let x = screenPosition[0];
      if (LABEL_PLACEMENT === "side") {
        const textWidth = fontSize * FONT_WIDTH_RATIO * text.length;
        const offset = screenDiameter / 2 + SIDE_LABEL_GAP + textWidth / 2;
        // Normally the text goes to the right of the node, but the library hides any label whose
        // centre falls outside the viewport — so a node near the right edge is flipped to its left
        // rather than losing its label.
        x += (x + offset + textWidth / 2 > viewportWidth) ? -offset : offset;
      }

      labels.push({
        id: `${pointIndex}`,
        text: text,
        x: x,
        y: screenPosition[1] + fontSize / 2,
        fontSize: fontSize,
        // No padding and no chip background: the node (or the canvas) is the background.
        padding: {left: 0, top: 0, right: 0, bottom: 0},
        style: this.labelStyle,
        // Inside a node the text takes its contrast from that node; beside it, from the theme.
        color: LABEL_PLACEMENT === "inside" ? this.data.pointLabelColors[pointIndex] : this.labelTextColor,
        // Most connected nodes win when two labels overlap.
        weight: this.data.pointDegrees[pointIndex],
        opacity: 1
      });
    });

    this.labelRenderer.setLabels(labels);
    // `true` hides the lighter of two labels that overlap. Inside a node that only happens while
    // the simulation is still untangling; beside a node it is the normal way clutter is kept down.
    this.labelRenderer.draw(true);
  }

  private onPointClicked(index: number, flowComponent: NetscopeCommonFlowviewComponent, graphData) {
    // In drag mode a click is the tail of a drag gesture, not a selection.
    if (flowComponent.draggableNodesEnabled) return;

    const uuid = this.data.pointIds[index];
    flowComponent.askShowSidePanel(
      `Flows of ${this.data.pointNames[index]}`,
      `where src_resource_uuid = '${uuid}' or dst_resource_uuid = '${uuid}' `,
      uuid,
      graphData.flowsRepository.nodeMap.get(uuid)
    );
    flowComponent.askRefreshAngular();
  }

  private onLinkClicked(linkIndex: number, flowComponent: NetscopeCommonFlowviewComponent) {
    const endpoints = this.data.linkEndpoints[linkIndex];
    if (endpoints === undefined) return;

    const [sourceUuid, targetUuid] = endpoints.map((i) => this.data.pointIds[i]);
    flowComponent.askShowSidePanel(
      `Flows between ${this.data.pointNames[endpoints[0]]} and ${this.data.pointNames[endpoints[1]]}`,
      `where (src_resource_uuid = '${sourceUuid}' and dst_resource_uuid = '${targetUuid}') or (dst_resource_uuid = '${sourceUuid}' and src_resource_uuid = '${targetUuid}') `
    );
    flowComponent.askRefreshAngular();
  }

  override zoomInVisualisation = (_flowComponent: NetscopeCommonFlowviewComponent) => {
    this.cosmosGraph?.zoom(this.cosmosGraph.getZoomLevel() * 1.3, 600);
  }

  override zoomOutVisualisation = (_flowComponent: NetscopeCommonFlowviewComponent) => {
    this.cosmosGraph?.zoom(this.cosmosGraph.getZoomLevel() / 1.3, 600);
  }

  override recenterVisualisation = (_flowComponent: NetscopeCommonFlowviewComponent) => {
    this.cosmosGraph?.fitView(600);
  }

  /**
   * Writes the graph to a PNG, the way the SigmaJS renderer does. The picture is made of two layers:
   * cosmos.gl draws the points and the links on a WebGL canvas, and the labels are DOM elements
   * stacked above it, so both are painted onto a canvas of their own.
   *
   * The WebGL canvas can be read back at any moment because luma.gl, which cosmos.gl draws through,
   * creates its context with 'preserveDrawingBuffer': the last frame drawn is still in the buffer.
   */
  exportAsImage = () => {
    const graphCanvas = this.container?.querySelector("canvas") as HTMLCanvasElement;

    if (graphCanvas == undefined || graphCanvas.width == 0 || graphCanvas.height == 0) {
      console.warn("[clusters-viewer] the graph was not drawn yet, there is nothing to export");
      return;
    }

    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = graphCanvas.width;
    exportCanvas.height = graphCanvas.height;

    const context = exportCanvas.getContext("2d");

    // The canvas of the graph is transparent where nothing was drawn, and a PNG with an alpha channel
    // shows up on a black background in most viewers: the background of the view is painted first.
    context.fillStyle = this.flowComponent?.isDarkMode ? "#222222" : "#FFFFFF";
    context.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    context.drawImage(graphCanvas, 0, 0);

    this.drawLabels(context, graphCanvas);

    exportCanvas.toBlob((blob) => {
      if (blob == undefined) {
        console.warn("[clusters-viewer] the graph could not be turned into an image");
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.setAttribute("href", url);
      link.setAttribute("download", "netscope-clusters.png");
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    }, "image/png");
  }

  /**
   * Paints the labels over the picture of the graph. They are read from the DOM rather than built
   * again: which of them are drawn is decided by the label renderer, as it hides the lighter of two
   * labels that overlap, and only the elements it left visible should end up in the picture.
   *
   * The elements are placed in CSS pixels over the canvas, while its drawing buffer is in device
   * pixels, hence the scale between the two.
   */
  private drawLabels(context: CanvasRenderingContext2D, graphCanvas: HTMLCanvasElement) {
    if (this.labelsDiv == undefined) {
      return;
    }

    const canvasRect = graphCanvas.getBoundingClientRect();
    const scale = canvasRect.width > 0 ? graphCanvas.width / canvasRect.width : 1;

    context.textAlign = "center";
    context.textBaseline = "middle";
    context.lineJoin = "round";

    for (const element of Array.from(this.labelsDiv.querySelectorAll("div"))) {
      // A label is a div holding text and nothing else. Anything holding other elements is a layer
      // the label renderer added around them, whose text is the text of every label at once.
      if (element.children.length > 0) {
        continue;
      }

      const text = (element.textContent ?? "").trim();

      if (text.length == 0) {
        continue;
      }

      // A label that lost an overlap is still in the DOM, made fully transparent
      const style = window.getComputedStyle(element);
      if (style.display == "none" || style.visibility == "hidden"
          || Number.parseFloat(style.opacity) < 0.05) {
        continue;
      }

      const rect = element.getBoundingClientRect();
      if (rect.width == 0 || rect.height == 0) {
        continue;
      }

      const x = (rect.left + rect.width / 2 - canvasRect.left) * scale;
      const y = (rect.top + rect.height / 2 - canvasRect.top) * scale;

      context.font = `${style.fontWeight} ${Number.parseFloat(style.fontSize) * scale}px ${style.fontFamily}`;

      // Beside a node the text crosses links, and wears a halo on screen: it is stroked here, as the
      // shadow it is drawn with in the DOM has no equivalent on a canvas.
      if (this.labelHaloColor != undefined) {
        context.strokeStyle = this.labelHaloColor;
        // Half of the stroke falls outside the glyphs, so this is the 3 CSS pixels the shadow blurs
        // over on screen, whatever the size of the text
        context.lineWidth = Math.max(2, 3 * scale);
        context.strokeText(text, x, y);
      }

      context.fillStyle = style.color;
      context.fillText(text, x, y);
    }
  }

  override destroy(flowComponent: NetscopeCommonFlowviewComponent) {
    super.destroy(flowComponent);
    clearInterval(this.interval);
    clearTimeout(this.trackedLabelsRefreshTimeout);
    this.trackedLabelsRefreshTimeout = undefined;
    this.hoveredNeighbourhood = undefined;

    try {
      this.labelRenderer?.destroy();
      this.labelsDiv?.remove();
      this.cosmosGraph?.destroy();
    } catch (e) {
      console.error("There was an error cleaning the cosmos.gl context");
      console.error(e);
    }

    this.labelRenderer = undefined;
    this.labelsDiv = undefined;
    this.cosmosGraph = undefined;
  }
}
